import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './TerminalEaster.css';

const PROMPT = 'pedro@portfolio:~$';

const COMMANDS = {
  help: {
    output: [
      '  Available commands:',
      '',
      '  whoami          — about Pedro Riccio',
      '  skills          — list technical skills',
      '  ls projects     — list projects',
      '  cat contact.txt — contact info',
      '  uptime          — how long Pedro has been coding',
      '  sudo hire pedro — make the best decision of your life',
      '  neofetch        — system info',
      '  clear           — clear terminal',
      '  exit            — close terminal',
    ],
  },
  whoami: {
    output: [
      'Pedro Riccio',
      'Backend Engineer · Software Architect · IT Infrastructure Analyst',
      '',
      'Location  : Bahia, Brasil',
      'Status    : Open to opportunities',
      'Email     : pedroworkdev@gmail.com',
      'GitHub    : github.com/IIBladeII',
      'LinkedIn  : linkedin.com/in/pedro-riccio',
    ],
  },
  skills: {
    output: [
      'PROCESS LIST — active skills',
      '─────────────────────────────────────────────────────',
      'PID   NAME              STATUS     PROFICIENCY',
      '001   Python            RUNNING    ████████████ 95%',
      '002   Node.js/Express   RUNNING    ███████████  90%',
      '003   PostgreSQL/MySQL  RUNNING    ███████████  88%',
      '004   Linux/Bash        RUNNING    ████████████ 92%',
      '005   Docker            RUNNING    ████████     80%',
      '006   APIs REST         RUNNING    ████████████ 96%',
      '007   Flask             RUNNING    █████████    85%',
      '008   Git/CI-CD         RUNNING    ████████████ 90%',
      '009   Power BI          RUNNING    ███████      70%',
      '010   Cybersecurity     RUNNING    █████████    85%',
    ],
  },
  'ls projects': {
    output: [
      'total 23 projects',
      '',
      'drwxr-xr-x  backend/     API REST em Flask + JWT + PostgreSQL',
      'drwxr-xr-x  security/    Ferramentas de pen-test e auditoria',
      'drwxr-xr-x  automation/  Scripts de automação industrial (ERP)',
      'drwxr-xr-x  fullstack/   Sistema B2B completo com React + Node',
      'drwxr-xr-x  infra/       Configuração de servidores Linux',
      '',
      '> Use "portfolio.service --list" or scroll to #projects',
    ],
  },
  'cat contact.txt': {
    output: [
      '# contact.txt',
      '',
      'email      = pedroworkdev@gmail.com',
      'whatsapp   = +55 (73) 9 8832-1118',
      'github     = https://github.com/IIBladeII',
      'linkedin   = https://linkedin.com/in/pedro-riccio',
      'location   = Bahia, Brasil',
      'status     = available_for_hire=true',
    ],
  },
  neofetch: {
    output: [
      '    ██████████████    pedro@portfolio',
      '    ██ PEDRO.DEV ██   ─────────────────────────',
      '    ██████████████    OS       : PedroOS 3.1.0 LTS',
      '                      Kernel   : 6.8.0-backend-generic',
      '                      Uptime   : 5+ years',
      '                      Packages : 50+ delivered projects',
      '                      Shell    : python3 / bash / node',
      '                      Stack    : Python, Node, React, PostgreSQL',
      '                      Certs    : 13+ (Google, Alura)',
      '                      Memory   : ████████░░ 80% coffee',
      '                      CPU      : dedication@100%',
    ],
  },
  uptime: {
    output: [
      'System uptime: 5 years, 3 months, 12 days',
      '',
      'Start date    : 2020-01-01',
      'Current date  : 2025-04-03',
      'Load average  : 9.8 / 10 (commitment index)',
      '',
      'No crashes detected. System stable.',
    ],
  },
  'sudo hire pedro': {
    output: [
      '[sudo] password for recruiter: ••••••••',
      '',
      'Verifying credentials...',
      '✔ Identity confirmed',
      '✔ Portfolio reviewed',
      '✔ Skills validated',
      '',
      '🚀 CONGRATULATIONS — process hire-pedro.sh completed successfully!',
      '',
      'Next step: pedro@portfolio:~$ cat contact.txt',
    ],
    special: 'hire',
  },
  clear: { special: 'clear' },
  exit: { special: 'exit' },
};

const getResponse = (input) => {
  const trimmed = input.trim().toLowerCase();
  return COMMANDS[trimmed] || {
    output: [
      `bash: ${input}: command not found`,
      "Type 'help' to see available commands.",
    ],
    error: true,
  };
};

const TerminalEaster = ({ onClose }) => {
  const [history, setHistory] = useState([
    {
      type: 'output',
      lines: [
        "Welcome to pedro-os — type 'help' for available commands",
        '',
      ],
    },
  ]);
  const [input, setInput] = useState('');
  const [cmdHistory, setCmdHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [hireMode, setHireMode] = useState(false);
  const inputRef = useRef(null);
  const bodyRef = useRef(null);

  const scrollBottom = useCallback(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollBottom();
  }, [history, scrollBottom]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKey = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = Math.min(historyIdx + 1, cmdHistory.length - 1);
      setHistoryIdx(idx);
      setInput(cmdHistory[idx] || '');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const idx = Math.max(historyIdx - 1, -1);
      setHistoryIdx(idx);
      setInput(idx === -1 ? '' : cmdHistory[idx]);
    }
  };

  const run = (e) => {
    e.preventDefault();
    const cmd = input.trim();
    if (!cmd) return;

    setCmdHistory((prev) => [cmd, ...prev]);
    setHistoryIdx(-1);

    const res = getResponse(cmd);

    if (res.special === 'clear') {
      setHistory([]);
      setInput('');
      return;
    }

    if (res.special === 'exit') {
      onClose();
      return;
    }

    if (res.special === 'hire') setHireMode(true);

    setHistory((prev) => [
      ...prev,
      { type: 'cmd', text: cmd },
      { type: 'output', lines: res.output, error: res.error },
    ]);
    setInput('');
  };

  return (
    <AnimatePresence>
      <motion.div
        className={`terminal-easter${hireMode ? ' terminal-easter--hire' : ''}`}
        initial={{ opacity: 0, scale: 0.9, y: 40 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 40 }}
        transition={{ type: 'spring', damping: 20, stiffness: 260 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="te-header">
          <div className="te-dots">
            <button className="te-dot te-dot--red" onClick={onClose} title="Close" />
            <span className="te-dot te-dot--yellow" />
            <span className="te-dot te-dot--green" />
          </div>
          <span className="te-title">pedro@portfolio — bash — interactive</span>
          <button className="te-close" onClick={onClose}>✕</button>
        </div>

        <div className="te-body" ref={bodyRef} onClick={() => inputRef.current?.focus()}>
          {history.map((item, i) => (
            <div key={i}>
              {item.type === 'cmd' && (
                <div className="te-row te-row--cmd">
                  <span className="te-prompt">{PROMPT}</span>
                  <span className="te-cmd-text">{item.text}</span>
                </div>
              )}
              {item.type === 'output' && (
                <div className={`te-output${item.error ? ' te-output--error' : ''}`}>
                  {item.lines.map((line, j) => (
                    <div key={j} className="te-line">{line || '\u00a0'}</div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <form className="te-row te-row--input" onSubmit={run}>
            <span className="te-prompt">{PROMPT}</span>
            <input
              ref={inputRef}
              className="te-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="off"
              placeholder="type a command..."
            />
          </form>
        </div>

        <div className="te-footer">
          <span>TAB: autocomplete hint</span>
          <span>↑↓: history</span>
          <span>ESC / exit: close</span>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TerminalEaster;
